/**
 * TransferCoordinator — orchestrates the BitDrop application layer.
 *
 * This is the ONLY component that translates engine events into application state.
 * It owns:
 *  - Independent BitDrop sessionId generation (random, NOT the torrent infoHash)
 *  - sessionMap: engineTorrentId → Set<sessionId> correlation index
 *  - Zustand state updates (live runtime state)
 *  - History persistence (durable checkpoints only)
 *  - Share protocol packet encoding (for QR / legacy clients)
 *
 * It does NOT own:
 *  - Piece scheduling, disk I/O, DHT, NAT traversal, piece verification
 *  - Magnet URI generation (engine provides these)
 *  - TorrentIdentity computation (engine provides these)
 *
 * ── Identity model ────────────────────────────────────────────────────────────
 *
 *   sessionId         = BitDrop application identity  (random, opaque)
 *   torrentIdentity   = BitTorrent content identity   (from engine, never parsed by us)
 *   engineTorrentId   = Engine runtime correlation ID (maps events → sessionId)
 *   magnetUri         = Canonical portable address    (from engine, stored verbatim)
 *
 * ── Persistence model ────────────────────────────────────────────────────────
 *
 *   Zustand  → live runtime state (speed, peers, progress)
 *   History  → durable checkpoints (survives restart)
 *   Engine   → runtime torrent handles (in-memory only)
 *   engineTorrentId NOT persisted — rebuilt on engine reattachment
 *
 * ── Checkpoint scheduler ─────────────────────────────────────────────────────
 *
 *   Progress events update Zustand immediately without throttling.
 *   History writes are throttled to a 3-second MAXIMUM interval (not debounce).
 *   Terminal events (completed, failed, cancelled) flush immediately
 *   and cancel any pending progress checkpoint.
 *   Resume data written only when emitted by engine (torrent:resume_data).
 */

import { TransferSession, FileMetadata } from '@share/domain';
import type { TorrentIdentity } from '@share/domain';
import { useTransferStore } from '../state/transfer.store';
import { useConfigStore } from '../state/config.store';
import { AppCryptoEngine } from '@share/crypto';
import type { HistoryRepository, PersistedSession } from '@share/storage';
import type { ResolvedReceiveLink } from './resolve-receive-link';

import {
  serializePayload,
  createShareUrl,
  encodeCapabilities,
  parseMagnet,
} from '@share/networking';
import { AppConfig } from '../../shared/config/app-config';

import type {
  TorrentEngine,
  TorrentEvent,
  TorrentInputFile,
  TorrentDestination,
  Unsubscribe,
} from '@share/networking';

const crypto = new AppCryptoEngine();
const WEB_DESTINATION_PREFIX = 'web-directory:';

/** Maximum interval between progress checkpoints (ms). */
const CHECKPOINT_INTERVAL_MS = 3_000;

interface CheckpointState {
  lastSavedAt: number;
  timer: ReturnType<typeof setTimeout> | null;
}

/** Returned by startSendSession — the primary share artifact is magnetUri. */
export interface ShareSessionResult {
  /** BitDrop application session ID (random, opaque). */
  sessionId: string;
  /** BitTorrent content identity — from the engine, not parsed by BitDrop. */
  torrentIdentity: TorrentIdentity;
  /** Full magnet URI — the primary share artifact. UI copies this for sharing. */
  magnetUri: string;
  /** Optional metadata about the torrent contents. */
  torrentName?: string;
  torrentFile?: Uint8Array;
}

export interface ReceiveSessionResult {
  status: 'started' | 'already_seeding' | 'already_downloading' | 'already_completed' | 'resumed';
  sessionId: string;
  message?: string;
}

class TransferCoordinatorService {
  private history: HistoryRepository | null = null;
  private engine: TorrentEngine | null = null;
  private eventUnsub: Unsubscribe | null = null;

  /** Per-session checkpoint state (runtime-only). */
  private checkpoints = new Map<string, CheckpointState>();

  /**
   * Correlation index: engineTorrentId → Set<BitDrop sessionId>
   *
   * WHY Set not string: same torrent can be in multiple sessions simultaneously.
   *   Example: session A seeding, session B receiving the same content.
   *   Map<string, string> would silently overwrite session A when B is registered.
   *
   * WHY engineTorrentId not infoHash as a concept: separates "which engine runtime
   * object produced this event" from "what content does this session represent".
   * Currently engineTorrentId === torrentIdentity.v1 for both engines — that's an
   * engine implementation detail, not an architectural constraint.
   *
   * THIS IS A CORRELATION INDEX, NOT A BROADCAST INSTRUCTION.
   * Events are routed per-session based on session.direction.
   */
  private sessionMap = new Map<string, Set<string>>();

  initialize(history: HistoryRepository, engine: TorrentEngine): void {
    this.history = history;
    this.engine = engine;

    this.eventUnsub = this.engine.onEvent((event: TorrentEvent) => {
      this._handleEngineEvent(event);
    });
  }

  // ── Private: sessionMap management ────────────────────────────────────────

  private _registerSession(engineTorrentId: string, sessionId: string): void {
    let sessions = this.sessionMap.get(engineTorrentId);
    if (!sessions) {
      sessions = new Set<string>();
      this.sessionMap.set(engineTorrentId, sessions);
    }
    sessions.add(sessionId);
  }

  /** Find any sessionId registered under a given engineTorrentId (e.g. infoHash). */
  private _findSessionByEngineId(engineTorrentId: string): string | null {
    const sessions = this.sessionMap.get(engineTorrentId);
    if (!sessions || sessions.size === 0) return null;
    return sessions.values().next().value ?? null;
  }

  private _deregisterSession(engineTorrentId: string, sessionId: string): void {
    const sessions = this.sessionMap.get(engineTorrentId);
    if (!sessions) return;
    sessions.delete(sessionId);
    if (sessions.size === 0) this.sessionMap.delete(engineTorrentId);
  }

  // ── Private: checkpoint scheduler ─────────────────────────────────────────

  /**
   * Schedule a max-interval checkpoint for a progress event.
   * Fires at most once per CHECKPOINT_INTERVAL_MS for a given session.
   * If elapsed time >= interval, writes immediately.
   * Otherwise, schedules a write for the remaining time (if not already pending).
   */
  private _scheduleProgressCheckpoint(sessionId: string): void {
    const now = Date.now();
    let cp = this.checkpoints.get(sessionId);
    if (!cp) {
      cp = { lastSavedAt: 0, timer: null };
      this.checkpoints.set(sessionId, cp);
    }

    const elapsed = now - cp.lastSavedAt;

    if (elapsed >= CHECKPOINT_INTERVAL_MS) {
      if (cp.timer !== null) {
        clearTimeout(cp.timer);
        cp.timer = null;
      }
      cp.lastSavedAt = now;
      this._persistCurrentState(sessionId);
    } else if (cp.timer === null) {
      const remaining = CHECKPOINT_INTERVAL_MS - elapsed;
      cp.timer = setTimeout(() => {
        if (this.checkpoints.get(sessionId)) {
          this.checkpoints.get(sessionId)!.timer = null;
          this.checkpoints.get(sessionId)!.lastSavedAt = Date.now();
        }
        this._persistCurrentState(sessionId);
      }, remaining);
    }
  }

  /**
   * Flush immediately for terminal / status-change events.
   * Cancels any pending progress checkpoint to prevent stale overwrites.
   */
  private _flushImmediately(sessionId: string): void {
    const cp = this.checkpoints.get(sessionId);
    if (cp && cp.timer !== null) {
      clearTimeout(cp.timer);
      cp.timer = null;
    }
    if (cp) cp.lastSavedAt = Date.now();
    this._persistCurrentState(sessionId);
  }

  private _clearCheckpoint(sessionId: string): void {
    const cp = this.checkpoints.get(sessionId);
    if (cp && cp.timer !== null) clearTimeout(cp.timer);
    this.checkpoints.delete(sessionId);
  }

  private _persistCurrentState(sessionId: string): void {
    if (!this.history) return;
    const session = useTransferStore.getState().sessions[sessionId];
    if (!session) return;
    this.history.saveSession(this._toPersistedSession(session));
  }

  private async _storeDestinationRef(
    sessionId: string,
    destination: TorrentDestination
  ): Promise<string | undefined> {
    if (destination.kind === 'browser-directory') {
      if (typeof window === 'undefined') return undefined;

      const { IDBHandles } = require('@share/storage');
      const destinationRef = `${WEB_DESTINATION_PREFIX}${sessionId}`;
      await IDBHandles.saveHandle(destinationRef, destination.handle);
      return destinationRef;
    }

    if (destination.kind === 'native-path') {
      return destination.path;
    }

    return undefined;
  }

  /**
   * Convert live TransferSession to durable PersistedSession.
   * Strips runtime-only fields: speed, uploadSpeed, peersCount, seedersCount,
   * eta, engineTorrentId.
   */
  private _toPersistedSession(session: TransferSession): PersistedSession {
    return {
      id: session.id,
      torrentIdentity: session.torrentIdentity,   // required — engine-provided
      direction: session.direction,
      files: session.files,
      status: session.status,
      progress: session.progress,
      bytesTransferred: session.bytesTransferred,
      bytesUploaded: session.bytesUploaded ?? 0,
      totalBytes: session.totalBytes,
      magnetUri: session.magnetUri,
      destinationRef: session.destinationRef,
      // engineTorrentId NOT persisted — runtime-only, rebuilt on recovery
      revision: session.revision,
      createdAt: session.createdAt,
      updatedAt: session.updatedAt,
    };
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Create a torrent from files, seed it, and return the session result.
   *
   * Key invariant: sessionId is generated independently from infoHash.
   * The engine provides torrentIdentity and magnetUri — we do NOT parse its output.
   *
   * Returns magnetUri as the primary share artifact.
   * The UI copies magnetUri for sharing. The shortener (if used) is called
   * separately by the UI after this returns — never inside this method.
   */
  async startSendSession(files: any[], opts?: { expiry?: string }): Promise<ShareSessionResult> {
    if (!this.engine) throw new Error('Engine not initialized');

    const torrentFiles: TorrentInputFile[] = files.map((f: any) => ({
      name: f.name ?? f.id,
      size: f.size,
      mimeType: f.type ?? f.mimeType ?? 'application/octet-stream',
      browserFile: typeof File !== 'undefined' && f instanceof File ? f : undefined,
      nativePath: f.nativePath ?? undefined,
    }));

    // ── 1. Engine creates torrent and begins seeding ─────────────────────────
    // Engine provides: engineTorrentId, torrentIdentity, magnetUri
    // We consume these directly. No parsing. No reconstruction.
    let handle;
    try {
      handle = await this.engine.createAndSeed({ files: torrentFiles });
    } catch (err: any) {
      // WebTorrent throws "Cannot add duplicate torrent <infoHash>" when the
      // exact same file content is seeded twice. In that case, find the existing
      // active session and return it — no new torrent needed.
      // This is the industry-standard behavior (qBittorrent, Transmission, Deluge):
      // re-seeding identical content joins the existing swarm, not a new one.
      const msg: string = err?.message ?? '';
      const dupeMatch = msg.match(/[0-9a-f]{40}/i);
      if (dupeMatch) {
        const existingInfoHash = dupeMatch[0].toLowerCase();
        const existingSessionId = this._findSessionByEngineId(existingInfoHash);
        if (existingSessionId) {
          const existing = useTransferStore.getState().sessions[existingSessionId];
          if (existing?.magnetUri) {
            return {
              sessionId: existingSessionId,
              torrentIdentity: existing.torrentIdentity,
              magnetUri: existing.magnetUri,
            };
          }
        }
      }
      throw err;
    }

    // ── 2. Generate independent BitDrop sessionId ────────────────────────────
    // This is NOT the infoHash. It is a random, opaque application identifier.
    const sessionId = crypto.generateId(16);
    const shareId = crypto.generateId(16); // for legacy protocol payload

    // ── 3. Build session metadata ────────────────────────────────────────────
    const sessionFiles: FileMetadata[] = torrentFiles.map((f) => ({
      id: crypto.generateId(8),
      name: f.name,
      size: f.size,
      mimeType: f.mimeType,
    }));

    const totalBytes = sessionFiles.reduce((sum, f) => sum + f.size, 0);
    const now = Date.now();

    // Legacy protocol payload (for QR / old BitDrop clients — NOT the primary share artifact)
    const infoHashV1 = handle.torrentIdentity.v1 ?? '';
    const manifestObj = { version: 1, files: sessionFiles, createdAt: now };
    const manifestBytes = new TextEncoder().encode(JSON.stringify(manifestObj));
    const fullHash = await crypto.hashManifest(manifestBytes);
    const manifestHash = fullHash.substring(0, 40);
    const flags = encodeCapabilities(
      (this.engine as any).capabilities ?? {
        resume: false, background: false, folder: false,
        preview: true, stream: true, multiPeer: true,
      }
    );
    const serializedPayload = serializePayload(1, shareId, infoHashV1, manifestHash, flags);
    // legacyShareUrl kept for QR generation — not the primary share artifact
    createShareUrl(serializedPayload);

    // Call the link shortener API
    let finalShareUrl = handle.magnetUri;
    try {
      const res = await fetch(`${AppConfig.shareBaseUrl}/api/links/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: handle.magnetUri, expiry: opts?.expiry || '3d' })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.shortUrl) {
          finalShareUrl = data.shortUrl;
        }
      }
    } catch (e) {
      console.warn('[Coordinator] Failed to create short URL, falling back to magnet:', e);
    }

    // ── 4. Create TransferSession ────────────────────────────────────────────
    const newSession: TransferSession = {
      id: sessionId,                            // random BitDrop ID — NOT infoHash
      torrentIdentity: handle.torrentIdentity,  // from engine directly, not parsed
      engineTorrentId: handle.engineTorrentId,  // runtime correlation (not persisted)
      direction: 'send',
      files: sessionFiles,
      status: 'seeding',
      progress: 1,
      speed: 0,
      uploadSpeed: 0,
      peersCount: 0,
      seedersCount: 1,
      bytesTransferred: totalBytes,
      bytesUploaded: 0,
      totalBytes,
      eta: 0,
      createdAt: now,
      updatedAt: now,
      revision: 0,
      magnetUri: finalShareUrl,
    };

    // ── 5. Register, persist, return ─────────────────────────────────────────
    useTransferStore.getState().addSession(newSession);
    this._registerSession(handle.engineTorrentId, sessionId);

    if (this.history) {
      await this.history.saveSession(this._toPersistedSession(newSession));
      this.checkpoints.set(sessionId, { lastSavedAt: now, timer: null });
    }

    return {
      sessionId,
      torrentIdentity: handle.torrentIdentity,
      magnetUri: finalShareUrl,
      torrentName: handle.torrentName,
      torrentFile: handle.torrentFile,
    };
  }

  /**
   * Start receiving a transfer.
   *
   * Accepts a ResolvedReceiveLink — a canonical { magnetUri } that has already
   * been validated by resolveReceiveLink() in the application layer.
   *
   * The coordinator does NOT classify or parse raw user input. That responsibility
   * belongs to resolveReceiveLink() in packages/core/services/resolve-receive-link.ts.
   *
   * After the engine returns a handle, its torrentIdentity becomes authoritative.
   */
  async startReceiveSession(
    resolved: ResolvedReceiveLink,
    destination: TorrentDestination
  ): Promise<ReceiveSessionResult> {
    if (!this.engine) throw new Error('Engine not initialized');

    const { magnetUri } = resolved;

    // ── Defense-in-depth: assert the magnetUri is parseable ─────────────────
    // resolveReceiveLink() has already validated this. This guard ensures the
    // coordinator is independently correct if called from other paths.
    let candidateIdentity: TorrentIdentity;
    try {
      const parsed = parseMagnet(magnetUri);
      candidateIdentity = parsed.identity;
    } catch {
      throw new Error(`Coordinator received an invalid magnet URI: ${magnetUri}`);
    }

    // ── Self-download & Duplicate Check (BitTorrent Standard) ─────────────
    const candidateHash = candidateIdentity.v1?.toLowerCase();
    if (candidateHash) {
      const allSessions = Object.values(useTransferStore.getState().sessions);
      const existingSession = allSessions.find(
        (s) => s.torrentIdentity.v1?.toLowerCase() === candidateHash
      );

      if (existingSession) {
        if (existingSession.direction === 'send' || existingSession.status === 'seeding') {
          return {
            status: 'already_seeding',
            sessionId: existingSession.id,
            message: 'You are already seeding this file.',
          };
        }
        if (existingSession.status === 'completed' || existingSession.progress >= 1) {
          return {
            status: 'already_completed',
            sessionId: existingSession.id,
            message: 'You have already downloaded this file.',
          };
        }
        if (existingSession.status === 'connecting' || existingSession.status === 'transferring' || existingSession.status === 'completing') {
          return {
            status: 'already_downloading',
            sessionId: existingSession.id,
            message: 'Transfer is already active in your list.',
          };
        }
        if (existingSession.status === 'paused') {
          this.resumeSession(existingSession.id);
          return {
            status: 'resumed',
            sessionId: existingSession.id,
            message: 'Resumed existing transfer.',
          };
        }
      }
    }

    // ── 1. Generate independent BitDrop sessionId ────────────────────────────
    const sessionId = crypto.generateId(16);
    const now = Date.now();
    const destinationRef = await this._storeDestinationRef(sessionId, destination);

    const newSession: TransferSession = {
      id: sessionId,
      torrentIdentity: candidateIdentity,  // candidate until engine confirms
      direction: 'receive',
      files: [],
      status: 'connecting',
      progress: 0,
      speed: 0,
      bytesTransferred: 0,
      bytesUploaded: 0,
      totalBytes: 0,
      eta: 0,
      createdAt: now,
      updatedAt: now,
      revision: 0,
      magnetUri,
      destinationRef,
    };

    useTransferStore.getState().addSession(newSession);

    try {
      // ── 2. Engine downloads ────────────────────────────────────────────────
      const handle = await this.engine.download({ magnetUri }, destination);

      // ── 3. Engine identity is authoritative ───────────────────────────────
      // The engine has now actually connected to the swarm and resolved the torrent.
      // Its torrentIdentity and engineTorrentId override our candidate.
      this._registerSession(handle.engineTorrentId, sessionId);

      const confirmedMagnetUri = handle.magnetUri || magnetUri;
      const updates: Partial<TransferSession> = {
        torrentIdentity: handle.torrentIdentity,      // engine is authoritative
        engineTorrentId: handle.engineTorrentId,
        magnetUri: confirmedMagnetUri,
        destinationRef,
        revision: 1,
        updatedAt: Date.now(),
      };

      useTransferStore.getState().updateSession(sessionId, updates);

      const updatedSession: TransferSession = { ...newSession, ...updates };
      if (this.history) {
        await this.history.saveSession(this._toPersistedSession(updatedSession));
        this.checkpoints.set(sessionId, { lastSavedAt: Date.now(), timer: null });
      }

      return {
        status: 'started',
        sessionId,
      };
    } catch (err) {
      const failedAt = Date.now();
      const message = err instanceof Error ? err.message : String(err);

      // WebTorrent throws "Cannot add duplicate torrent <infoHash>" when the
      // same magnet is already active in the engine. In that case:
      //   1. Remove the orphan 'connecting' session we just added to Zustand/IDB.
      //   2. Surface the existing active session to the user (it's already in the list).
      const dupeMatch = message.match(/[0-9a-f]{40}/i);
      if (dupeMatch) {
        const existingInfoHash = dupeMatch[0].toLowerCase();
        const existingSessionId = this._findSessionByEngineId(existingInfoHash);
        if (existingSessionId) {
          // Clean up the orphan session silently — the existing session is already visible.
          useTransferStore.getState().removeSession(sessionId);
          if (this.history) {
            this.history.deleteSession(sessionId).catch(() => {});
          }
          // Don't throw — return existing active session
          return {
            status: 'already_downloading',
            sessionId: existingSessionId,
            message: 'Transfer is already active in your list.',
          };
        }
      }

      useTransferStore.getState().updateSession(sessionId, {
        status: 'failed',
        error: message,
        revision: 1,
        updatedAt: failedAt,
      });

      if (this.history) {
        await this.history.saveSession(
          this._toPersistedSession({
            ...newSession,
            status: 'failed',
            error: message,
            revision: 1,
            updatedAt: failedAt,
          })
        );
      }

      throw err;
    }
  }

  pauseSession(sessionId: string): void {
    const session = useTransferStore.getState().sessions[sessionId];
    // Only use engineTorrentId — same reason as cancelSession.
    // torrentIdentity.v1 is the protocol infoHash, not a runtime engine key.
    const engineId = session?.engineTorrentId ?? null;

    // Immediately reflect user intent in state
    const rev = (session?.revision ?? 0) + 1;
    useTransferStore.getState().updateSession(sessionId, {
      status: 'paused',
      speed: 0,
      uploadSpeed: 0,
      revision: rev,
      updatedAt: Date.now(),
    });
    if (this.history) this._flushImmediately(sessionId);

    if (this.engine && engineId) this.engine.pause(engineId);
  }

  resumeSession(sessionId: string): void {
    const session = useTransferStore.getState().sessions[sessionId];
    // Only use engineTorrentId — same reason as cancelSession.
    const engineId = session?.engineTorrentId ?? null;
    if (this.engine && engineId) this.engine.resume(engineId);
  }

  async cancelSession(sessionId: string, deleteData: boolean = false): Promise<void> {
    const session = useTransferStore.getState().sessions[sessionId];

    // IMPORTANT: only use engineTorrentId — the runtime handle the engine assigned.
    // torrentIdentity.v1 is the protocol infoHash and NOT a valid engine key unless
    // the engine has actually loaded the torrent. Using it as a fallback causes
    // "No torrent with id" errors for sessions recovered from IDB but not yet
    // attached to the engine (e.g. paused web receiver sessions after reload).
    const engineId = session?.engineTorrentId ?? null;

    if (this.engine && engineId) {
      await this.engine.remove(engineId, { deleteData });
    }

    if (engineId) this._deregisterSession(engineId, sessionId);
    this._clearCheckpoint(sessionId);

    // Remove from live Zustand store — card disappears from list immediately
    useTransferStore.getState().removeSession(sessionId);

    // Remove from durable history — does not persist across reload
    if (this.history) {
      this.history.deleteSession(sessionId).catch((err) => {
        console.warn('[Coordinator] Failed to delete session from history:', err);
      });
    }
  }

  /**
   * Re-register a recovered session after engine reattachment on app restart.
   * Called by bootstrap AFTER engine.restoreFromResumeData() or engine.download()
   * returns a new TorrentHandle. Rebuilds the sessionMap entry.
   */
  registerRecoveredSession(sessionId: string, engineTorrentId: string): void {
    this._registerSession(engineTorrentId, sessionId);
    useTransferStore.getState().updateSession(sessionId, { engineTorrentId });
  }

  // ── Private: Engine event handler ──────────────────────────────────────────

  destroy(): void {
    if (this.eventUnsub) {
      this.eventUnsub();
      this.eventUnsub = null;
    }
    for (const cp of this.checkpoints.values()) {
      if (cp.timer) {
        clearTimeout(cp.timer);
        cp.timer = null;
      }
    }
    this.checkpoints.clear();
  }

  private _handleEngineEvent = async (event: TorrentEvent): Promise<void> => {
    try {
      // event.id = engineTorrentId (the engine's runtime identifier for this torrent)
      const sessionIds = this.sessionMap.get(event.id);
      if (!sessionIds || sessionIds.size === 0) return;

      for (const sessionId of sessionIds) {
        const session = useTransferStore.getState().sessions[sessionId];
        if (!session) continue;
        this._routeEventToSession(sessionId, session, event);
      }
    } catch (err) {
      console.error('[TransferCoordinator] Unhandled error in engine event handler:', err);
    }
  };

  private _syncFiles(sessionFiles: FileMetadata[], eventFiles?: any[]): FileMetadata[] {
    if (sessionFiles.length > 0 || !eventFiles || eventFiles.length === 0) {
      return sessionFiles;
    }
    return eventFiles.map((f) => ({
      id: crypto.generateId(8),
      name: f.name,
      size: f.size ?? 0,
      mimeType: 'application/octet-stream',
    }));
  }

  private _routeEventToSession(
    sessionId: string,
    session: TransferSession,
    event: TorrentEvent
  ): void {
    const s = event.status;
    const isSend = session.direction === 'send';
    const now = Date.now();

    switch (event.type) {
      case 'torrent:metadata': {
        if (!s) break;
        const syncedFiles = this._syncFiles(session.files, s.files);
        if (syncedFiles !== session.files) {
          const rev = session.revision + 1;
          useTransferStore.getState().updateSession(sessionId, {
            files: syncedFiles,
            revision: rev,
            updatedAt: now,
          });
          this._flushImmediately(sessionId);
        }
        break;
      }

      case 'torrent:progress':
      case 'torrent:downloading': {
        if (!s) break;
        const isPaused = session.status === 'paused';
        const rev = session.revision + 1;
        useTransferStore.getState().updateSession(sessionId, {
          files:            this._syncFiles(session.files, s.files),
          progress:         isSend ? 1 : s.progress,
          speed:            isSend || isPaused ? 0 : s.downloadSpeed,
          uploadSpeed:      isPaused ? 0 : s.uploadSpeed,
          peersCount:       s.peers,
          seedersCount:     isSend ? (s.seeds || 1) : s.seeds,
          bytesTransferred: isSend ? s.totalBytes : s.downloaded,
          totalBytes:       s.totalBytes,
          eta:              isSend || isPaused ? 0 : (s.eta === Infinity ? 0 : s.eta),
          status:           isPaused
                              ? 'paused'
                              : isSend
                              ? 'seeding'
                              : (s.progress >= 1 ? 'completing' : 'transferring'),
          revision: rev, updatedAt: now,
        });
        this._scheduleProgressCheckpoint(sessionId);
        break;
      }

      case 'torrent:seeding': {
        // seeding = upload-only mode
        // SEND session  → stay 'seeding'
        // RECV session  → download done, now re-seeding = treat as 'completed'
        if (!s) break;
        const rev = session.revision + 1;
        useTransferStore.getState().updateSession(sessionId, {
          files:            this._syncFiles(session.files, s.files),
          status:           isSend ? 'seeding' : 'completed',
          progress:         1,
          speed:            0,
          uploadSpeed:      s.uploadSpeed,
          peersCount:       s.peers,
          seedersCount:     s.seeds || 1,
          bytesTransferred: s.totalBytes,
          totalBytes:       s.totalBytes,
          eta:              0,
          magnetUri:        s.magnetUri || session.magnetUri,
          revision: rev, updatedAt: now,
        });
        this._flushImmediately(sessionId);
        break;
      }

      case 'torrent:completed': {
        // completed = download finished
        // RECV sessions only — SEND sessions never 'complete' in torrent terms
        if (isSend) break;
        if (!s) break;
        const rev = session.revision + 1;
        useTransferStore.getState().updateSession(sessionId, {
          files:            this._syncFiles(session.files, s.files),
          status:           'completed',
          progress:         1,
          speed:            s.downloadSpeed,
          uploadSpeed:      s.uploadSpeed,
          peersCount:       s.peers,
          seedersCount:     s.seeds,
          bytesTransferred: s.totalBytes,
          totalBytes:       s.totalBytes,
          eta:              0,
          magnetUri:        s.magnetUri || session.magnetUri,
          revision: rev, updatedAt: now,
        });
        this._flushImmediately(sessionId);

        // Dispatch notification if enabled
        try {
          const cfg = useConfigStore.getState().config;
          if (cfg?.notificationsEnabled && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            const fileName = session.files?.[0]?.name;
            new Notification('BitDrop Transfer Complete', {
              body: fileName ? `"${fileName}" finished downloading.` : 'Your download has finished.',
              icon: '/icons/favicon.svg',
            });
          }
        } catch {
          // ignore notification errors
        }
        break;
      }

      case 'torrent:paused': {
        const rev = session.revision + 1;
        useTransferStore.getState().updateSession(sessionId, {
          status: 'paused', revision: rev, updatedAt: now,
        });
        this._flushImmediately(sessionId);
        break;
      }

      case 'torrent:resumed': {
        const rev = session.revision + 1;
        // Direction-aware status: seeders go back to 'seeding', receivers to 'transferring'.
        // Completed sessions (receiver that finished + is now seeding) stay 'completed'.
        const resumedStatus =
          session.status === 'completed'
            ? 'completed'
            : isSend
            ? 'seeding'
            : 'transferring';
        useTransferStore.getState().updateSession(sessionId, {
          status: resumedStatus,
          speed: 0,       // will be updated by next progress event
          uploadSpeed: 0, // will be updated by next progress event
          revision: rev,
          updatedAt: now,
        });
        this._flushImmediately(sessionId);
        break;
      }

      case 'torrent:error': {
        console.error('[Coordinator] Engine error:', event.error);
        const rev = session.revision + 1;
        useTransferStore.getState().updateSession(sessionId, {
          status: 'failed',
          error: event.error,
          revision: rev,
          updatedAt: now,
        });
        this._flushImmediately(sessionId);
        break;
      }

      case 'torrent:resume_data': {
        // Resume data written atomically — not bundled with progress
        if (event.resumeDataBase64 && this.history) {
          useTransferStore.getState().updateSession(sessionId, { updatedAt: now });
          this.history.updateResumeData(sessionId, {
            format: 'libtorrent.fastresume.v1',
            version: 1,
            data: event.resumeDataBase64,
          });
        }
        break;
      }
    }
  }
}

export const TransferCoordinator = new TransferCoordinatorService();
